import { z } from "zod"
import { STORE_CODES } from "./stores"

export const OUTBOUND_EVENT_TYPE = { nutritionImageChanged: "nutrition_image.changed" } as const
// Pub/Sub retains messages for 30 days. Allow another 30 days to fetch images.
export const OUTBOUND_IMAGE_RETENTION_MS = 60 * 24 * 60 * 60 * 1000
export const OUTBOUND_DELIVERY_STATE = {
  pending: "pending",
  sending: "sending",
  acknowledged: "acknowledged",
} as const
export const OUTBOUND_ATTEMPT_OUTCOME = {
  sending: "sending",
  acknowledged: "acknowledged",
  failed: "failed",
  abandoned: "abandoned",
} as const

/** Encoded-byte identity appears in the immutable canonical object name. */
export const outboundCurrentImageSchema = z
  .object({
    url: z.string().url(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    contentSource: z.literal("pim"),
  })
  .strict()
  .superRefine((image, context) => {
    const url = new URL(image.url)
    if (
      url.protocol !== "https:" ||
      url.search ||
      url.hash ||
      !url.pathname.includes(image.sha256)
    ) {
      context.addIssue({
        code: "custom",
        message: "image URL must identify immutable encoded bytes without query or fragment",
      })
    }
  })
export const outboundNutritionEventSchema = z
  .object({
    eventId: z.string().uuid(),
    type: z.literal(OUTBOUND_EVENT_TYPE.nutritionImageChanged),
    schemaVersion: z.literal(1),
    occurredAt: z.string().datetime({ offset: true }),
    sku: z.string().min(1).max(255),
    store: z.enum(STORE_CODES),
    revision: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    currentImage: outboundCurrentImageSchema.nullable(),
  })
  .strict()
export type OutboundNutritionEvent = z.infer<typeof outboundNutritionEventSchema>
export type OutboundCurrentImage = z.infer<typeof outboundCurrentImageSchema>
const outboundCursorPartsSchema = z.tuple([
  z.string().datetime({ offset: true }),
  z.string().uuid(),
])
export const outboundListQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  before: z
    .string()
    .max(100)
    .refine(
      (cursor) => outboundCursorPartsSchema.safeParse(cursor.split("|")).success,
      "before must be a cursor returned by outbound-events list"
    )
    .optional(),
  pending: z.enum(["true", "false"]).optional(),
})
export const outboundDispatchControlSchema = z.object({ paused: z.boolean() }).strict()
export const outboundCutoverSchema = z
  .object({
    requestId: z.string().uuid(),
    publishBeforeConsumerReady: z.literal(true),
    previousWorkersDrained: z.literal(true),
  })
  .strict()
